import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import Form from 'global/components/form';
import { get, set, first } from 'global/lib/lodash';
import { Paper, InputAdornment, Button, Icon, LinearProgress, IconButton } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import { CommunityIcon, DivisionIcon } from 'global/components/PerformIcons';
import CloseIcon from '@material-ui/icons/Close';
import Alert from '@material-ui/lab/Alert';
import sendData from 'redux/actions/sendData';
import ConfirmModal from 'global/components/confirm';
import withRouter from 'react-router-dom/withRouter';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useScreen } from 'global/styles';
import useStyles from './ReportAssessmentInfo.styles';
import DropRealms from 'global/components/droprealms';
import { UploadFiles } from 'redux/actions/imageUploader';
import { getAccountSetting } from 'redux/actions/userLogin';
import { SETTINGS } from 'feature/admin/account-settings';
import { useUser } from 'global/hooks/useUser';
import { useLoaded } from 'global/hooks/useLoaded';
import { userHasPermissions } from 'redux/actions/userLogin';
import { getOnlyTiersFromScorecards } from '../ReportFab';
import { ensureArray } from '../../../global/utils';

const DATEFORMAT = 'l';
const MODDATEFORMAT = 'l h:mma';

export default withRouter((props) => {
  const { assessment, userCanEdit, printView, isTemplate } = props;
  const { data, loading } = assessment;
  const { me } = useUser();
  const [canEdit, setCanEdit] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState();
  const [confirmModal, setConfirmModal] = useState();
  const [realmId, setRealmId] = useState(null);
  const [realm, setRealm] = useState(get(data, 'realm'));
  const formRef = useRef();

  const { isMobileXS } = useScreen();
  const classes = useStyles({ isMobile: isMobileXS });

  const _realm = get(data, 'realm');
  const realmTier = get(realm, 'tier');

  const users = useRetrieveData(
    { url: '/users' },
    {
      waitFor: realmId,
      build: ['users'],
      params: { filter: { content_realm_id: realmId } },
      useCache: { reload: 1 },
    }
  );

  const createdBy = get(data, 'authors.0');

  const defaultAccess = getAccountSetting(SETTINGS.assessmentAccess);

  useEffect(() => {
    setRealm(_realm);
  }, [_realm]);

  useEffect(() => {
    if (realm) {
      setRealmId(realm.id);
    }
  }, [realm]);

  const toggleEdit = async () => {
    if (canEdit) {
      formRef.current.clearState();
    }
    setCanEdit(!canEdit);
  };

  const changeRealm = (obj = {}) => {
    const r = obj[Object.keys(obj)[0]];
    if (r && r.id !== realm.id) {
      setConfirmModal({
        title: 'Confirm',
        content: 'Photos uploaded on the photos page will be deleted if you update the location of your assessment. Do you want to continue?',
        onOk: () => {
          setRealm(r);
          setConfirmModal();
        },
        onCancel: () => {
          setConfirmModal();
        },
      });
    }
  };

  const formatData = async (values) => {
    const relationships = {};
    const scorecards = [];
    const editors = get(
      values,
      'editors',
      get(data, 'editors', [])
    );
    const viewers = get(
      values,
      'viewers',
      get(data, 'viewers', [])
    );
    const assignees = ensureArray(get(values, 'assignees', get(data, 'assignees', [])));

    const vis = get(values, 'generalVisibility', 'normal');

    for (let k in values) {
      if (k.startsWith('scorecard')) {
        scorecards.push(values[k]);
      }
    }

    const startDateFormat = 'YYYY-MM-DD';
    const createFormat = 'YYYY-MM-DD HH:mm:ss';

    const attributes = {
      name: get(values, 'name'),
      start_date: moment(get(values, 'startDate', get(data, 'startDate', moment()))).format(startDateFormat),
      end_date: moment(get(values, 'endDate', get(data, 'endDate', moment()))).format(startDateFormat),
      completion_date: get(values, 'completionDate') ? moment(get(values, 'completionDate')).format(startDateFormat) : null,
      visibility: vis === 'normal' ? 1 : vis === 'global' ? 2 : 0,
      status: isTemplate ? get(assessment, 'data.status') : get(values, 'status'),
      description: get(values, 'description'),
      'updated-at': moment().format(createFormat),
      realm_id: get(realm, 'id', '').toString(),
      media: [...get(assessment, 'data.media', []), ...get(values, 'files', [])],
    };

    if (editors) {
      set(relationships, 'editors', {
        data: editors.map((e) => ({ type: 'users', id: get(e, 'id') })),
      });
    }

    if (viewers) {
      set(relationships, 'viewers', {
        data: viewers.map((e) => ({ type: 'users', id: get(e, 'id') })),
      });
    }

    if (assignees) {
      set(relationships, 'assignees', {
        data: assignees.map((e) => ({ type: 'users', id: get(e, 'id') })),
      });
    }

    const ret = {
      data: {
        type: isTemplate ? 'assessment-templates' : 'assessments',
        id: get(data, 'id', '').toString(),
        attributes,
        relationships,
      },
    };

    await UploadFiles(ret, { attributeType: 'media', isImage: false, collection_name: 'debrief-files' });

    return ret;
  };

  const formSubmit = async (values) => {
    try {
      setFormLoading(true);
      const d = await formatData(values);
      await sendData({
        url: isTemplate ? `/assessment-templates/${data.id}` : `/assessments/${data.id}`,
        method: 'patch',
        data: d,
      });
      props.assessment.update();
      setFormLoading(false);
      setCanEdit(false);
    } catch (err) {
      setFormLoading(false);
      setError(`${err.message}. Please try again.`);
    }
  };

  const dateInputs = canEdit
    ? [
        [
          {
            id: 'startDate',
            type: 'KeyboardDatePicker',
            style: { margin: '10px 5px' },
            defaultValue: () => moment(get(data, 'startDate')),
            props: {
              label: 'Visit Start',
            },
          },
          {
            id: 'endDate',
            type: 'KeyboardDatePicker',
            style: { margin: '10px 5px' },
            defaultValue: () => moment(get(data, 'endDate')),
            props: {
              label: 'Visit End',
            },
          },
        ],
        {
          id: 'completionDate',
          type: 'KeyboardDatePicker',
          defaultValue: () => (get(data, 'completionDate') ? moment(get(data, 'completionDate')) : ''),
          props: {
            label: 'Delivery Date',
          },
        },
      ]
    : [
        {
          id: 'datess',
          type: 'TextField',
          defaultValue: () => {
            const sDate = get(data, 'startDate');
            const eDate = get(data, 'endDate');
            const dstring = sDate == eDate ? `${moment(sDate).format(DATEFORMAT)}` : `${moment(sDate).format(DATEFORMAT)} - ${moment(eDate).format(DATEFORMAT)}`;
            const cDate = get(data, 'completionDate');
            return dstring + (cDate ? `\nReport Delivery: ${moment(get(data, 'completionDate')).format(DATEFORMAT)}` : '');
          },
          props: {
            label: 'Dates',
            disabled: !canEdit,
            multiline: true,
          },
        },
      ];

  const onDelete = () => {
    setConfirmModal({
      title: 'Delete Assessment',
      content: 'Are you sure you want to delete this assessment?',
      onCancel: () => setConfirmModal(),
      onOk: async () => {
        try {
          setFormLoading(true);
          setConfirmModal();
          await sendData({
            url: isTemplate ? `/assessment-templates/${data.id}` : `/assessments/${data.id}`,
            method: 'patch',
            data: {
              data: {
                type: isTemplate ? 'assessment-templates' : 'assessments',
                id: data.id,
                attributes: {
                  archived: true,
                },
              },
            },
          });
          props.history.push('/reports');
        } catch (err) {
          setError(`Error: ${err.message}`);
          setFormLoading(false);
        }
      },
    });
  };

  const onDeleteMedia = (file) => {
    setConfirmModal({
      title: 'Delete Media',
      content: `Are you sure you want to delete ${file.name}?`,
      onCancel: () => setConfirmModal(),
      onOk: async () => {
        try {
          setFormLoading(true);
          setConfirmModal();
          await sendData({
            url: `/media/${file.id}`,
            method: 'delete',
          });
          await assessment.update();
          setFormLoading(false);
        } catch (err) {
          setError(`Error: ${err.message}`);
          setFormLoading(false);
        }
      },
    });
  };

  const onUnlock = () => {
    setConfirmModal({
      title: 'Unlock Assessment',
      content: `Unlocking an assessment can create data issues because the person who locked it can make edits offline that conflict with online edits. We suggest you only unlock assessments when you're sure that this person will not make further edits.`,
      okayText: 'Unlock',
      onCancel: () => setConfirmModal(),
      onOk: async () => {
        try {
          setFormLoading(true);
          await sendData({
            url: `/assessments/${data.id}`,
            method: 'patch',
            data: {
              data: {
                id: data.id,
                type: 'assessments',
                attributes: {
                  locked: 0,
                }
              }
            }
          })
          await assessment.update();
        } catch (err) {
        } finally {
          setFormLoading(false);
          setConfirmModal();
        }
      }
    })
  }

  const allUsers = [me, ...users.data];

  useLoaded(props, [loading, users.loading]);

  return !data || loading || users.loading ? (
    <LinearProgress />
  ) : (
    <div className={classes.container}>
      {error && (
        <Alert
          severity="error"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => {
                setError();
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}

      {!printView && (
        <div className={classes.buttonContainer}>
          {!formLoading && userCanEdit && (
            <Button color={'secondary'} variant="contained" onClick={toggleEdit} startIcon={<Icon>{canEdit ? 'close' : 'edit'}</Icon>}>
              {'Edit'}
            </Button>
          )}
          {!formLoading && canEdit && userCanEdit && (
            <Button color={'secondary'} className={classes.deleteButton} variant="contained" onClick={onDelete}>
              <Icon>delete</Icon>
              {'Delete'}
            </Button>
          )}
          {!formLoading && !!data.locked && userHasPermissions(['manageAccount']) && (
            <Button color={'secondary'} style={{ marginLeft: 8 }} variant="contained" onClick={onUnlock} startIcon={<Icon>{'lock'}</Icon>}>
              {'Unlock'}
            </Button>
          )}
        </div>
      )}

      <Form
        loading={formLoading}
        showAllScreens={true}
        formStyle={classes.formStyle}
        onSubmit={formSubmit}
        canSubmit={canEdit}
        ref={formRef}
        submitText="Save Changes"
        screens={[
          [
            {
              title: 'General Info',
              titleStyle: classes.titleStyle,
              screenContainerStyle: isMobileXS ? classes.screenContainerMobile : classes.screenContainer,
              screenContainer: {
                props: {
                  className: classes.screenWidth,
                },
              },
              inputs: [
                {
                  props: {
                    label: 'Location',
                    disabled: !canEdit,
                  },
                  component: (
                    <DropRealms
                      title={get(realm, 'name')}
                      openToSelected={true}
                      modalTitle={'SET LOCATION'}
                      onlyTiers={isTemplate ? ['builder','division','community'] : getOnlyTiersFromScorecards(data.scorecards)}
                      hideShow={true}
                      showModify={canEdit}
                      singleSelect
                      currentRealm={realm}
                      onUpdate={changeRealm}
                      realms={[realm]}
                    />
                  ),
                },
                {
                  id: 'name',
                  type: 'TextField',
                  defaultValue: () => get(data, 'name', ''),
                  props: {
                    label: 'Title',
                    disabled: !canEdit,
                  },
                },
                ...dateInputs,
                !isTemplate && {
                  id: 'status',
                  type: 'Select',
                  defaultValue: () => get(data, 'status'),
                  data: [
                    { title: 'Draft', value: 'draft' },
                    { title: 'Review', value: 'review' },
                    { title: 'Final', value: 'published' },
                  ],
                  props: {
                    label: 'Status',
                    disabled: !canEdit,
                  },
                },
                {
                  id: 'description',
                  type: 'RichText',
                  defaultValue: () => get(data, 'description', ''),
                  props: {
                    label: 'Description',
                    placeholder: 'Description',
                    disabled: !canEdit,
                  },
                },
                !printView && {
                  id: 'files',
                  type: 'DropZone',
                  props: {
                    disabled: !canEdit,
                    label: 'Files',
                    dropzoneText: (
                      <>
                        Drag and Drop or Click to Browse
                        <Icon className={classes.dropIcon}>folder_open</Icon>
                      </>
                    ),
                    onDeleteFile: (file) => {
                      onDeleteMedia(file);
                    },
                    initialFiles: get(assessment, 'data.media', []),
                    fullWidth: true,
                    filesLimit: 100,
                    useChipsForPreview: true,
                    maxFileSize: 315922080,
                    dropzoneClass: classes.dropZone,
                  },
                },
              ].filter((f) => f),
            },
            {
              title: 'Detailed Info',
              collapsable: { collapsed: !printView },
              titleStyle: classes.titleStyle,
              screenContainerStyle: isMobileXS ? classes.screenContainerMobile : classes.screenContainer,
              screenContainer: {
                props: {
                  className: classes.screenWidth,
                },
              },
              inputs: [
                {
                  id: 'locationname',
                  type: 'TextField',
                  defaultValue: () => `${get(data, 'realm.parent.name')} - ${get(realm, 'name')}`,
                  props: {
                    label: 'Location',
                    disabled: true,
                    InputProps: {
                      startAdornment: <InputAdornment position="start">{realmTier === 'division' ? <DivisionIcon /> : realmTier === 'community' ? <CommunityIcon /> : <HomeIcon />}</InputAdornment>,
                    },
                  },
                },
                {
                  id: 'criteria',
                  type: 'TextField',
                  defaultValue: () =>
                    get(data, 'scorecards', [])
                      .map((sc) => get(sc, 'name', 'Unknown'))
                      .join('\n'),
                  props: {
                    label: 'Criteria',
                    disabled: true,
                    multiline: true,
                  },
                },
                {
                  id: 'created',
                  type: 'TextField',
                  defaultValue: () => `${moment(get(data, 'createdAt')).format(MODDATEFORMAT)} - ${get(createdBy, 'name', 'UNKNOWN')}`,
                  props: {
                    label: 'Created',
                    disabled: true,
                  },
                },
                {
                  id: 'modified',
                  type: 'TextField',
                  defaultValue: () => `${moment(get(data, 'updatedAt')).format(MODDATEFORMAT)}`,
                  props: {
                    label: 'Modified',
                    disabled: true,
                  },
                },
              ],
            },
          ],
          !printView && {
            title: 'Access',
            titleStyle: classes.titleStyle,
            screenContainerStyle: isMobileXS ? classes.screenContainerMobile : classes.screenContainer,
            screenContainer: {
              component: Paper,
              props: {
                elevation: 5,
                square: true,
                className: classes.accessContainer,
              },
            },
            inputs: [
              {
                id: 'assignees',
                type: 'UserSelect',
                data: () => allUsers,
                defaultValue: () => first(get(data, 'assignees', [])),
                props: {
                  label: 'Assignee',
                  helperText: isTemplate ? 'This user will be the default Assignee for all assessments created from this template' : 'These users can edit this assessment',
                  disabled: !canEdit,
                  size: 'small',
                  required: get(realm, 'tier') === 'lot',
                  multiple: false,
                  helperText: (fs) => {
                    const keys = {
                      draft: 'This user won’t see scores or highlights until the status is set to “Review”',
                      review: 'This user can see scores and highlights since the status is “Review”',
                      published: 'This user can view this assessment',
                      template: 'This user will be the default Assignee for all assessments created from this template',
                    }

                    if (isTemplate) { return keys.template }

                    return get(keys, get(assessment, 'data.status'), keys.draft);
                  }
                },
              },
              {
                id: 'editors',
                type: 'UserSelect',
                data: () => allUsers,
                defaultValue: () => get(data, 'editors', []),
                props: {
                  label: 'Editors',
                  helperText: isTemplate ? 'These users can use and edit this template and will be assigned as Editors on all assessments created from this template' : 'These users can edit this assessment',
                  disabled: !canEdit,
                  size: 'small',
                },
              },
              {
                id: 'viewers',
                type: 'UserSelect',
                data: () => allUsers,
                defaultValue: () => get(data, 'viewers', []),
                props: {
                  label: 'Viewers',
                  size: 'small',
                  helperText: (formState) => {
                    if (isTemplate) {
                      return 'These users will be assigned as Viewers on all assessments created from this template'
                    }
                    const statusText =
                      get(formState, 'status') === 'draft'
                        ? ` but won't see scores or highlights until the status is set to "Review"`
                        : get(formState, 'status') === 'review'
                        ? ` and can see scores and highlights since the status is "Review"`
                        : '';
                    return `These users can view this assessment${statusText}`;
                  },
                  disabled: !canEdit,
                },
              },
              {
                id: 'generalVisibility',
                type: 'ToggleButton',
                options: [
                  { title: 'NORMAL', value: 'normal' },
                  { title: 'GLOBAL', value: 'global' },
                  { title: 'HIDDEN', value: 'hidden' },
                ],
                defaultValue: () => get(data, 'visibility', 'normal'),
                props: {
                  exclusive: true,
                  label: 'General Visibility',
                  disabled: !canEdit,
                  helperText: (formState) => {
                    const gVis = get(formState, 'generalVisibility');
                    if (isTemplate) {
                      const keys = {
                        normal: `(default) Assessments created from this template will be assigned normal access: all users with access to that new assessment's location will see it.`,
                        global: `Assessments created from this template will be assigned global access: all users in the company will see it.`,
                        hidden: `Assessments created from this template will be assigned hidden access: no users will see it except the Author and those assigned as the Assignee, Editors, or Viewers.`
                      }

                      return get(keys, gVis);
                    }

                    const statusText = get(formState, 'status') !== 'published' ? ` However, they won't see scores or highlights until the status is set to "Final".` : '';

                    let str;

                    const getText = (key, text) => {
                      let t = '';
                      if (key === defaultAccess) {
                        t += '(default) ';
                      }
                      t += text;
                      return t;
                    };

                    if (gVis === 'normal') {
                      str = `All users with access to ${get(realm, 'name', '[Realm Name]')} can view this assessment.${statusText}`;
                    } else if (gVis === 'global') {
                      str = `All users in the company can view this assessment.${statusText}`;
                    } else if (gVis === 'hidden') {
                      str = 'No users can see this assessment except those assigned above.';
                    } else {
                      str = '';
                    }

                    return getText(gVis, str);
                  },
                },
              },
            ],
          },
        ].filter((f) => f)}
      />
      <ConfirmModal {...confirmModal} />
    </div>
  );
});
